import {
  getBrowserTimezone,
  isValidTimezone,
  normaliseManualOffset,
} from "./timezoneUtils";

export const TIME_PREFERENCES_STORAGE_KEY = "marinebytes.time-preferences";
export const TIME_PREFERENCES_EVENT = "marinebytes:time-preferences-changed";

export const DEFAULT_TIME_PREFERENCES = Object.freeze({
  showLocalTime: false,
  selectedTimezone: "",
  timeFormat: "24",
  dateFormat: "DD/MM/YYYY",
  autoDetectTimezone: true,
  manualOffset: null,
  timeSource: "browser",
});

export const normaliseTimePreferences = (preferences = {}) => {
  const selectedTimezone = isValidTimezone(preferences.selectedTimezone)
    ? preferences.selectedTimezone
    : "";
  const manualOffset = normaliseManualOffset(preferences.manualOffset);
  const legacyTimeSource = preferences.autoDetectTimezone
    ? "browser"
    : selectedTimezone
      ? "timezone"
      : "browser";
  const timeSource = ["browser", "timezone", "manual"].includes(
    preferences.timeSource,
  )
    ? preferences.timeSource
    : legacyTimeSource;
  const base = {
    ...DEFAULT_TIME_PREFERENCES,
    ...preferences,
    showLocalTime: Boolean(preferences.showLocalTime),
    timeFormat: preferences.timeFormat === "12" ? "12" : "24",
    dateFormat: ["DD/MM/YYYY", "MM/DD/YYYY", "YYYY-MM-DD"].includes(
      preferences.dateFormat,
    )
      ? preferences.dateFormat
      : DEFAULT_TIME_PREFERENCES.dateFormat,
  };

  if (timeSource === "manual" && manualOffset) {
    return {
      ...base,
      timeSource: "manual",
      autoDetectTimezone: false,
      selectedTimezone: "",
      manualOffset,
    };
  }

  if (timeSource === "timezone") {
    return {
      ...base,
      timeSource: "timezone",
      autoDetectTimezone: false,
      selectedTimezone,
      manualOffset: null,
    };
  }

  return {
    ...base,
    timeSource: "browser",
    autoDetectTimezone: true,
    selectedTimezone: "",
    manualOffset: null,
  };
};

export const loadTimePreferences = () => {
  try {
    return normaliseTimePreferences(
      JSON.parse(window.localStorage.getItem(TIME_PREFERENCES_STORAGE_KEY) || "{}"),
    );
  } catch {
    return { ...DEFAULT_TIME_PREFERENCES };
  }
};

// This is the current application settings storage adapter. It is intentionally
// isolated so it can be replaced with the settings API once that endpoint exists.
export const saveTimePreferences = (preferences) => {
  const savedPreferences = normaliseTimePreferences(preferences);
  window.localStorage.setItem(
    TIME_PREFERENCES_STORAGE_KEY,
    JSON.stringify(savedPreferences),
  );
  window.dispatchEvent(
    new CustomEvent(TIME_PREFERENCES_EVENT, { detail: savedPreferences }),
  );
  return savedPreferences;
};

export const getEffectiveTimezone = (preferences) => {
  const savedPreferences = normaliseTimePreferences(preferences);
  if (savedPreferences.timeSource === "manual") {
    return savedPreferences.manualOffset;
  }
  if (savedPreferences.timeSource === "browser") {
    const browserTimezone = getBrowserTimezone();
    return isValidTimezone(browserTimezone) ? browserTimezone : "";
  }
  return savedPreferences.selectedTimezone;
};
